import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCampaignsList } from '@/hooks/useCampaigns';
import type { DispatchStatus } from '@/types/api.types';
import type { DispatchFilters } from '@/hooks/useDispatches';

const ALL_STATUSES: DispatchStatus[] = ['pending', 'sent', 'delivered', 'read', 'failed'];

const STATUS_LABEL: Record<DispatchStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

interface Props {
  filters: DispatchFilters;
  onChange: (filters: DispatchFilters) => void;
}

export function DispatchesFilters({ filters, onChange }: Props) {
  const { data } = useCampaignsList({ pageSize: 100 });
  const campaigns = data?.data ?? [];

  function set(patch: Partial<DispatchFilters>) {
    onChange({ ...filters, ...patch, page: 1 });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Status pills */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => set({ status: undefined })}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            !filters.status
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input text-muted-foreground hover:text-foreground'
          }`}
        >
          Todos
        </button>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => set({ status: s })}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filters.status === s
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input text-muted-foreground hover:text-foreground'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {/* Campaign select */}
      <Select
        value={filters.campaignId ?? 'all'}
        onValueChange={(v) => set({ campaignId: v === 'all' ? undefined : v })}
      >
        <SelectTrigger className="h-8 w-48 text-xs">
          <SelectValue placeholder="Campanha" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas as campanhas</SelectItem>
          {campaigns.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Date range */}
      <div className="flex items-center gap-1">
        <Input
          type="date"
          className="h-8 w-36 text-xs"
          value={filters.from ?? ''}
          onChange={(e) => set({ from: e.target.value || undefined })}
        />
        <span className="text-xs text-muted-foreground">até</span>
        <Input
          type="date"
          className="h-8 w-36 text-xs"
          value={filters.to ?? ''}
          onChange={(e) => set({ to: e.target.value || undefined })}
        />
      </div>
    </div>
  );
}
